/**
 * Canvas UI Constraints - Single source of truth for ReactFlow canvas configuration
 *
 * Related Requirements:
 * - FR-001: Canvas pan/zoom configuration
 * - FR-002: Grid step size for snap-to-grid
 * - FR-005: Zoom range constraints (10% to 500%)
 *
 * Usage:
 * Import these constants in ReactFlowCanvas.tsx to ensure consistent UX
 */

// FR-005: Zoom range constraints (10% minimum, 500% maximum)
export const ZOOM_MIN = 0.1;  // 10% zoom out
export const ZOOM_MAX = 5.0;  // 500% zoom in
export const DEFAULT_VIEWPORT_ZOOM = 0.5; // 50% initial zoom (makes blocks smaller for easier drag-and-drop)

// FR-002: Grid configuration for snap-to-grid behavior
export const GRID_STEP_SIZE = 16; // 16px grid (default)
export const GRID_DOT_SIZE = 1;   // Visual grid dot size

// Connection snap radius configuration
// Controls how close the cursor must be to a handle to start/snap a connection
// Default ReactFlow value: ~20-30px
// Current setting: 60px for comfortable handle targeting
export const CONNECTION_RADIUS = 60; // 60px snap radius (2-3x larger than default)

// FR-001: Pan configuration
// Left-click drag = pan canvas, Shift+drag = box select
// Two-finger scroll / scroll wheel also pans, Middle-click and Space+drag pan too
export const PAN_ON_DRAG = true; // Left-click drag = pan canvas
export const PAN_ON_SCROLL = true; // Two-finger scroll / scroll wheel = pan
export const PAN_ON_SCROLL_MODE = 'free' as const; // Pan in any direction
export const PAN_ON_SCROLL_SPEED = 1; // Default scroll pan speed

// FR-001: Zoom configuration (Figma-like)
// Pinch-to-zoom on trackpad, Cmd/Ctrl+scroll = zoom (auto with panOnScroll)
export const ZOOM_ON_PINCH = true; // Pinch gesture on trackpad = zoom
export const ZOOM_ON_SCROLL = false; // Scroll = pan (not zoom); Cmd+scroll auto-zooms
export const ZOOM_ON_DOUBLE_CLICK = false; // No zoom on double-click (double-click = open editor)

// FR-001: Multi-selection configuration
export const MULTI_SELECTION_KEYS = ['Meta', 'Ctrl'] as const; // Cmd/Ctrl for multi-select

// FR-001: Delete key configuration
export const DELETE_KEY_CODES = ['Backspace', 'Delete'] as const;

// Performance: Animation durations
export const ZOOM_ANIMATION_DURATION = 200;  // ms for zoom in/out
export const FIT_VIEW_ANIMATION_DURATION = 800; // ms for fit view
export const FOCUS_ANIMATION_DURATION = 800;    // ms for focus on screen

// MiniMap configuration
export const MINIMAP_ENABLED = true;
export const MINIMAP_ZOOM_STEP = 10;

// Background configuration
export const BACKGROUND_VARIANT = 'dots' as const;
export const BACKGROUND_GAP = GRID_STEP_SIZE; // Match grid size

// Selection mode: Shift+drag = box select
export const SELECTION_MODE = 'partial' as const; // Partial selection (touching selection box is enough)
export const SELECTION_ON_DRAG = true;            // Box select enabled
export const SELECTION_KEY_CODE = 'Shift';        // Shift required for box selection

// Attribution
export const ATTRIBUTION_POSITION = 'top-right' as const;
